/**
 * ProfitLossScreen — the money question: revenue − COGS − expenses = profit,
 * over any range, with a previous-period comparison and an honest disclosure of
 * how much of the period has a cost basis.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import {
  presetRange,
  toDateInputValue,
  startOfDay,
  lastIncludedInstant,
  type RangePreset as Preset,
} from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadProfitAndLoss } from '@/reports/pnl/pnlDataSource';
import { buildProfitAndLoss, type ProfitLoss } from '@/reports/pnl/profitAndLoss';
import { escapeHtml } from '@/utils/escapeHtml';

const pct = (fraction: number) => `${(fraction * 100).toFixed(1)}%`;

// ─── Component ───────────────────────────────────────────────────────────────

export function ProfitLossScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date())));
  const [customTo, setCustomTo]     = useState(toDateInputValue(new Date()));
  const [data, setData]             = useState<ProfitLoss | null>(null);
  const [previous, setPrevious]     = useState<ProfitLoss | null>(null);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);

  const range = useCallback(
    (): [Date, Date] => presetRange(preset, { from: customFrom, to: customTo }),
    [preset, customFrom, customTo],
  );

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      // Previous equal-length window, immediately before this one.
      const span = to.getTime() - from.getTime();
      const prevFrom = new Date(from.getTime() - span);
      const [current, prior] = await Promise.all([
        loadProfitAndLoss(activeStoreId, from, to),
        loadProfitAndLoss(activeStoreId, prevFrom, from),
      ]);
      setData(buildProfitAndLoss(current));
      setPrevious(buildProfitAndLoss(prior));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void load(); }, [load]);

  const rangeStamp = (): string => {
    const [from, to] = range();
    return `${toDateInputValue(from)}_${toDateInputValue(lastIncludedInstant(to))}`;
  };

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const rows = statementRows(data);
      const csv = toCsv(rows, [
        { header: 'Line', value: (r) => r.label },
        { header: 'Amount', value: (r) => r.amount },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `ProfitLoss_${rangeStamp()}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const [from, to] = range();
      await pdf.saveFromHtml({
        html: buildPnlHtml(data, from, to),
        defaultFileName: `ProfitLoss_${rangeStamp()}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'week', 'month', 'custom'] as Preset[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPreset(p)}
            className={cn(
              'rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle',
            )}
          >
            {p === 'today' ? 'Today' : p === 'week' ? 'Last 7 days' : p === 'month' ? 'This month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : (
        <>
          {/* Cost-basis disclosure — the honesty line */}
          {data.linesWithoutCost > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 px-4 py-3">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning-ink" />
              <div className="text-body text-fg-secondary">
                <span className="font-medium text-fg-primary">
                  {pct(data.linesWithoutCostShare)} of lines have no cost basis
                </span>{' '}
                ({data.linesWithoutCost} of {data.lineCount}), covering {pct(data.revenueWithoutCostShare)} of
                sale value. Those sales add revenue but no COGS, so gross profit here is an{' '}
                <span className="font-medium">upper bound</span>. Cost is captured going forward from each
                new sale — historical lines can’t be back-filled.
              </div>
            </div>
          )}

          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Net revenue" value={formatCurrency(data.netRevenue)}
              delta={previous && data.netRevenue - previous.netRevenue} />
            <KPI label="Gross profit" value={formatCurrency(data.grossProfit)} sub={pct(data.grossMargin)}
              delta={previous && data.grossProfit - previous.grossProfit} />
            <KPI label="Expenses" value={formatCurrency(data.totalExpenses)}
              delta={previous && data.totalExpenses - previous.totalExpenses} deltaGoodWhenDown />
            <KPI label="Net profit" value={formatCurrency(data.netProfit)} sub={pct(data.netMargin)} accent
              delta={previous && data.netProfit - previous.netProfit} />
          </div>

          {/* The statement */}
          <Card title="Profit & Loss statement">
            <table className="w-full text-body">
              <tbody>
                <StatementRow label="Gross sales (ex-GST)" amount={data.grossRevenue} />
                {data.returns > 0 && (
                  <StatementRow label={`Less: returns (${data.creditNoteCount} credit note${data.creditNoteCount !== 1 ? 's' : ''})`} amount={-data.returns} warn />
                )}
                <StatementRow label="Net revenue" amount={data.netRevenue} strong />
                <StatementRow label="Less: cost of goods sold" amount={-data.cogs} />
                <StatementRow label={`Gross profit (${pct(data.grossMargin)})`} amount={data.grossProfit} strong divider />

                {data.expensesByCategory.length > 0 && (
                  <tr>
                    <td colSpan={2} className="pt-3 pb-1 text-label uppercase text-fg-tertiary">Operating expenses</td>
                  </tr>
                )}
                {data.expensesByCategory.map((e) => (
                  <StatementRow key={e.name} label={e.name} amount={-e.amount} indent />
                ))}
                {data.totalExpenses > 0 && (
                  <StatementRow label="Total expenses" amount={-data.totalExpenses} />
                )}

                <StatementRow
                  label={`Net profit (${pct(data.netMargin)})`}
                  amount={data.netProfit}
                  strong
                  divider
                  accent={data.netProfit >= 0}
                  warn={data.netProfit < 0}
                />
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function KPI({
  label, value, sub, accent, delta, deltaGoodWhenDown,
}: {
  label: string; value: string; sub?: string; accent?: boolean;
  delta?: number | null | false; deltaGoodWhenDown?: boolean;
}) {
  const showDelta = typeof delta === 'number' && Math.abs(delta) >= 0.01;
  const up = typeof delta === 'number' && delta > 0;
  const good = typeof delta === 'number' && (deltaGoodWhenDown ? delta < 0 : delta > 0);
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', accent ? 'text-accent-ink' : 'text-fg-primary')}>
        {value}
      </div>
      <div className="mt-0.5 flex items-center gap-2">
        {sub && <span className="text-label text-fg-tertiary">{sub}</span>}
        {showDelta && (
          <span className={cn('flex items-center gap-0.5 text-label tabular-nums', good ? 'text-success-ink' : 'text-warning-ink')}>
            {up ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
            {formatCurrency(Math.abs(delta as number))}
          </span>
        )}
        {(sub || showDelta) && <span className="text-label text-fg-tertiary/70">vs prev.</span>}
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

function StatementRow({
  label, amount, strong, warn, accent, indent, divider,
}: {
  label: string; amount: number; strong?: boolean; warn?: boolean;
  accent?: boolean; indent?: boolean; divider?: boolean;
}) {
  return (
    <tr className={cn(divider && 'border-t border-stroke-hairline/15')}>
      <td className={cn('py-1.5', indent && 'pl-4',
        strong ? 'font-medium text-fg-primary' : 'text-fg-secondary',
        warn && 'text-warning-ink', accent && 'text-accent-ink')}>
        {label}
      </td>
      <td className={cn('py-1.5 text-right tabular-nums',
        strong ? 'font-medium text-fg-primary' : 'text-fg-secondary',
        warn && 'text-warning-ink', accent && 'text-accent-ink')}>
        {formatCurrency(amount)}
      </td>
    </tr>
  );
}

// ─── Export shaping ────────────────────────────────────────────────────────────

interface StatementLine { label: string; amount: number }

function statementRows(d: ProfitLoss): StatementLine[] {
  const rows: StatementLine[] = [
    { label: 'Gross sales (ex-GST)', amount: d.grossRevenue },
  ];
  if (d.returns > 0) rows.push({ label: 'Returns', amount: -d.returns });
  rows.push({ label: 'Net revenue', amount: d.netRevenue });
  rows.push({ label: 'Cost of goods sold', amount: -d.cogs });
  rows.push({ label: 'Gross profit', amount: d.grossProfit });
  for (const e of d.expensesByCategory) rows.push({ label: `Expense: ${escapeHtml(e.name)}`, amount: -e.amount });
  if (d.totalExpenses > 0) rows.push({ label: 'Total expenses', amount: -d.totalExpenses });
  rows.push({ label: 'Net profit', amount: d.netProfit });
  return rows;
}

function buildPnlHtml(d: ProfitLoss, from: Date, toExclusive: Date): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const fmtDate = (dt: Date) => dt.toLocaleDateString('en-IN');
  const to = lastIncludedInstant(toExclusive);
  const row = (label: string, amount: number, opts: { strong?: boolean } = {}) =>
    `<tr${opts.strong ? ' style="font-weight:700"' : ''}><td>${label}</td><td style="text-align:right">${fmt(amount)}</td></tr>`;

  const expenseRows = d.expensesByCategory
    .map((e) => `<tr><td style="padding-left:20px">${escapeHtml(e.name)}</td><td style="text-align:right">-${fmt(e.amount)}</td></tr>`)
    .join('');

  const coverage = d.linesWithoutCost > 0
    ? `<p style="color:#b45309;font-size:12px">Note: ${(d.linesWithoutCostShare * 100).toFixed(1)}% of lines (${d.linesWithoutCost} of ${d.lineCount}) have no cost basis, covering ${(d.revenueWithoutCostShare * 100).toFixed(1)}% of sale value — gross profit is an upper bound.</p>`
    : '';

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}
table{width:100%;border-collapse:collapse;font-size:13px;margin-top:12px}td{padding:6px 8px;border-bottom:1px solid #eee}
.tot td{border-top:2px solid #333;font-weight:700}</style></head>
<body>
<h1>Profit &amp; Loss</h1>
<p style="color:#888">${fmtDate(from)} – ${fmtDate(to)}</p>
<table>
${row('Gross sales (ex-GST)', d.grossRevenue)}
${d.returns > 0 ? row(`Less: returns (${d.creditNoteCount} credit note${d.creditNoteCount !== 1 ? 's' : ''})`, -d.returns) : ''}
${row('Net revenue', d.netRevenue, { strong: true })}
${row('Less: cost of goods sold', -d.cogs)}
${row(`Gross profit (${(d.grossMargin * 100).toFixed(1)}%)`, d.grossProfit, { strong: true })}
${expenseRows}
${d.totalExpenses > 0 ? row('Total expenses', -d.totalExpenses) : ''}
<tr class="tot"><td>Net profit (${(d.netMargin * 100).toFixed(1)}%)</td><td style="text-align:right">${fmt(d.netProfit)}</td></tr>
</table>
${coverage}
<p style="margin-top:40px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
